import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Button, PageHeader } from '../../components/ui';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STATUS_COLOR = {
  sent:    '#555',
  opened:  '#3498DB',
  clicked: '#9B59B6',
  replied: '#2ECC71',
  bounced: '#E74C3C',
};

// Reads stored bounceType from backend first (most accurate).
// Falls back to heuristic only for old records without bounceType stored.
const classifyBounce = (row) => {
  if (!row.bounced) return null;
  if (row.bounceType === 'hard') return 'hard';
  if (row.bounceType === 'soft') return 'soft';
  if (row.opened || row.clicked || row.replied) return 'soft';
  return 'soft'; // safe default — never assume hard without confirmation
};

export default function CampaignReport() {
  const { id } = useParams();
  const [report, setReport]               = useState(null);
  const [loading, setLoading]             = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { fetchReport(); }, [id]);

  const fetchReport = async () => {
    try {
      const r = await api.get(`/campaigns/${id}/report`);
      setReport(r.data);
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const campaignAction = async (action) => {
    setActionLoading(true);
    try {
      await api.post(`/campaigns/${id}/action`, { action });
      toast.success(`Campaign ${action === 'start' ? 'started' : action === 'pause' ? 'paused' : 'stopped'}`);
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadSent = async () => {
    try {
      const r = await api.get(`/campaigns/${id}/export/sent`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `campaign-sent.csv`; a.click();
      toast.success('Downloaded!');
    } catch { toast.error('Download failed'); }
  };

  const downloadAll = async () => {
    try {
      const r = await api.get(`/campaigns/${id}/export/all`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `campaign-all-contacts.csv`; a.click();
      toast.success('Downloaded!');
    } catch { toast.error('Download failed'); }
  };

  if (loading) return <div style={{ color: '#555', textAlign: 'center', padding: 60 }}>Loading report...</div>;
  if (!report)  return <div style={{ color: '#E74C3C', textAlign: 'center', padding: 60 }}>Report not found</div>;

  const { campaign: c, rows, totals } = report;

  // Use backend-computed counts if present, else compute from rows
  const hardBounces = totals.hardBounced ?? rows.filter(r => classifyBounce(r) === 'hard').length;
  const softBounces = totals.softBounced ?? rows.filter(r => classifyBounce(r) === 'soft').length;
  const sendFailed  = totals.sendFailed  ?? 0;

  const pieData = [
    { name: 'Opened',      value: totals.opened  || 0 },
    { name: 'Clicked',     value: totals.clicked || 0 },
    { name: 'Replied',     value: totals.replied || 0 },
    { name: 'Hard Bounce', value: hardBounces },
    { name: 'Soft Bounce', value: softBounces },
    { name: 'Sent Only',   value: Math.max(0, (totals.sent || 0) - (totals.opened || 0) - (totals.bounced || 0)) },
  ].filter(d => d.value > 0);

  const PIE_COLORS = ['#3498DB', '#9B59B6', '#2ECC71', '#E74C3C', '#F39C12', '#555'];
  const rate = (n) => totals.sent > 0 ? ((n / totals.sent) * 100).toFixed(1) : '0.0';

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Link to="/client/campaigns" style={{ color: '#555', fontSize: 13, textDecoration: 'none' }}>← Campaigns</Link>
        <span style={{ color: '#333' }}>/</span>
        <span style={{ color: '#ccc', fontSize: 13 }}>{c.name}</span>
      </div>

      {/* Header */}
      <PageHeader title={c.name} subtitle={`Campaign Report · ${c.type}`} action={
        <div style={{ display: 'flex', gap: 8 }}>
          {(c.status === 'draft' || c.status === 'paused' || c.status === 'scheduled') && (
            <Button size="sm" loading={actionLoading}
              style={{ background: 'rgba(46,204,113,0.1)', color: '#2ECC71', border: '1px solid rgba(46,204,113,0.3)' }}
              onClick={() => campaignAction('start')}>▶ Start</Button>
          )}
          {c.status === 'active' && (
            <Button size="sm" loading={actionLoading}
              style={{ background: 'rgba(243,156,18,0.1)', color: '#F39C12', border: '1px solid rgba(243,156,18,0.3)' }}
              onClick={() => campaignAction('pause')}>⏸ Pause</Button>
          )}
          {(c.status === 'active' || c.status === 'paused') && (
            <Button size="sm" loading={actionLoading}
              style={{ background: 'rgba(231,76,60,0.1)', color: '#E74C3C', border: '1px solid rgba(231,76,60,0.3)' }}
              onClick={() => campaignAction('stop')}>⏹ Stop</Button>
          )}
          <Button variant="secondary" onClick={downloadAll} size="sm">⬇ All Contacts CSV</Button>
          <Button onClick={downloadSent} size="sm">⬇ Sent Emails CSV</Button>
        </div>
      } />

      {/* ── 8 stat cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8,1fr)', gap: 12, marginBottom: 24 }}>
        {[
          ['📤', 'Sent',        totals.sent    || 0, '#2ECC71'],
          ['👀', 'Opened',      totals.opened  || 0, '#3498DB'],
          ['🖱️','Clicked',     totals.clicked || 0, '#9B59B6'],
          ['💬', 'Replied',     totals.replied || 0, '#2ECC71'],
          ['⚠️', 'Bounced',    totals.bounced || 0, '#E74C3C'],
          ['🔴', 'Hard Bounce', hardBounces,         '#E74C3C'],
          ['🟡', 'Soft Bounce', softBounces,         '#F39C12'],
          ['❌', 'Send Failed', sendFailed,          '#888888'],
        ].map(([icon, label, val, color]) => (
          <Card key={label} style={{ textAlign: 'center', padding: '16px 8px' }}>
            <div style={{ fontSize: 20 }}>{icon}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color, marginTop: 6 }}>{val?.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 2 }}>{label}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2, fontWeight: 600 }}>
              {label === 'Send Failed' ? '—' : `${rate(val)}%`}
            </div>
          </Card>
        ))}
      </div>

      {/* ── Info banners ── */}
      {(totals.bounced > 0 || sendFailed > 0) && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {hardBounces > 0 && (
            <div style={{ flex: 1, minWidth: 200, padding: '12px 16px', background: 'rgba(231,76,60,0.07)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 22 }}>🔴</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#E74C3C' }}>Hard Bounce — {hardBounces}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Permanent failure — invalid or non-existent email address. Do not retry.</div>
              </div>
            </div>
          )}
          {softBounces > 0 && (
            <div style={{ flex: 1, minWidth: 200, padding: '12px 16px', background: 'rgba(243,156,18,0.07)', border: '1px solid rgba(243,156,18,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 22 }}>🟡</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#F39C12' }}>Soft Bounce — {softBounces}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Temporary failure — mailbox full or server issue. May succeed on retry.</div>
              </div>
            </div>
          )}
          {sendFailed > 0 && (
            <div style={{ flex: 1, minWidth: 200, padding: '12px 16px', background: 'rgba(136,136,136,0.07)', border: '1px solid rgba(136,136,136,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 22 }}>❌</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#888' }}>Send Failed — {sendFailed}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Rejected before delivery — check your sending account configuration.</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Charts ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <Card>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>📊 Engagement Breakdown</h3>
          {pieData.length === 0
            ? <div style={{ textAlign: 'center', padding: 40, color: '#555' }}>No events recorded yet</div>
            : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={85} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {pieData.map((entry, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#111', border: '1px solid rgba(46,204,113,0.2)', borderRadius: 8, color: '#fff', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )
          }
          {totals.bounced > 0 && (
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8, fontSize: 11 }}>
              <span style={{ color: '#E74C3C' }}>🔴 Hard: {hardBounces}</span>
              <span style={{ color: '#F39C12' }}>🟡 Soft: {softBounces}</span>
              {sendFailed > 0 && <span style={{ color: '#888' }}>❌ Failed: {sendFailed}</span>}
            </div>
          )}
        </Card>

        <Card>
          <h3 style={{ fontWeight: 700, marginBottom: 20 }}>📈 Rates</h3>
          {[
            ['Open Rate',        rate(totals.opened),  '#3498DB'],
            ['Click Rate',       rate(totals.clicked), '#9B59B6'],
            ['Reply Rate',       rate(totals.replied), '#2ECC71'],
            ['Bounce Rate',      rate(totals.bounced), '#E74C3C'],
            ['Hard Bounce Rate', totals.sent > 0 ? ((hardBounces / totals.sent) * 100).toFixed(1) : '0.0', '#E74C3C'],
            ['Soft Bounce Rate', totals.sent > 0 ? ((softBounces / totals.sent) * 100).toFixed(1) : '0.0', '#F39C12'],
          ].map(([label, val, color]) => (
            <div key={label} style={{ marginBottom: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#888' }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color }}>{val}%</span>
              </div>
              <div style={{ background: '#1a1a1a', borderRadius: 4, height: 6 }}>
                <div style={{ width: `${Math.min(parseFloat(val), 100)}%`, height: '100%', borderRadius: 4, background: color, transition: 'width 0.5s' }} />
              </div>
            </div>
          ))}
        </Card>
      </div>

      {/* ── Per-contact table ── */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid rgba(46,204,113,0.08)' }}>
          <span style={{ fontWeight: 700 }}>Per-Contact Results ({rows.length})</span>
          <Button size="sm" variant="secondary" onClick={downloadSent}>⬇ Export CSV</Button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(46,204,113,0.05)', borderBottom: '1px solid rgba(46,204,113,0.08)' }}>
                {['Email','Name','Company','Sent','Opened','Clicked','Replied','Bounced','Bounce Type','Sent At'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#555', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0
                ? <tr><td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#555' }}>No email events recorded yet. Start the campaign to see results.</td></tr>
                : rows.map((r, i) => {
                    const bounceType       = classifyBounce(r);
                    const failedBeforeSend = r.bounced && !r.sentAt;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <td style={{ padding: '10px 14px', color: '#2ECC71' }}>{r.email}</td>
                        <td style={{ padding: '10px 14px', color: '#ccc' }}>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}</td>
                        <td style={{ padding: '10px 14px', color: '#888' }}>{r.company || '—'}</td>
                        {['sent','opened','clicked','replied','bounced'].map(k => (
                          <td key={k} style={{ padding: '10px 14px', textAlign: 'center' }}>
                            <span style={{ color: r[k] ? STATUS_COLOR[k] : '#333', fontWeight: r[k] ? 700 : 400, fontSize: 16 }}>
                              {r[k] ? '✓' : '·'}
                            </span>
                          </td>
                        ))}
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          {failedBeforeSend ? (
                            <span style={{ background: 'rgba(136,136,136,0.12)', color: '#888', border: '1px solid rgba(136,136,136,0.3)', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>❌ Failed</span>
                          ) : bounceType === 'hard' ? (
                            <span style={{ background: 'rgba(231,76,60,0.12)', color: '#E74C3C', border: '1px solid rgba(231,76,60,0.3)', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>🔴 Hard</span>
                          ) : bounceType === 'soft' ? (
                            <span style={{ background: 'rgba(243,156,18,0.12)', color: '#F39C12', border: '1px solid rgba(243,156,18,0.3)', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>🟡 Soft</span>
                          ) : (
                            <span style={{ color: '#333' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#555', fontSize: 11 }}>
                          {r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}
                        </td>
                      </tr>
                    );
                  })
              }
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
